import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Paperclip,
  Pencil,
  Plus,
  Repeat,
  Trash2,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router';
import {
  expenseCategories,
  expenseTaxTreatment,
  formatCents,
  formatDate,
  MAINTENANCE_KIND_FROM_EXPENSE,
  parseEuroToCents,
  rentalTiming,
  todayIso,
  type Expense,
  type ExpenseCategory,
  type ExpenseTemplate,
} from '@vuokramani/shared';
import { Section, pageStyles as ps } from '../../components/page.tsx';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  Notice,
  Panel,
  SelectField,
  TextField,
} from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import {
  useCreateExpenseTemplate,
  useDeleteExpense,
  useDeleteExpenseTemplate,
  useExpenseTemplates,
  useSaveExpense,
  useUpdateExpenseTemplate,
} from '../../lib/expenses.ts';
import { addMonths, monthLabel } from '../../lib/invoices.ts';
import { fileUrl, useLeases, useUploadFiles } from '../../lib/tenancy.ts';
import { CATEGORY_LINK } from '../../lib/taxLinks.ts';
import s from '../shared/invoices.module.css';

const euros = (cents: number, lang: string) =>
  (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2 });

/** Vuoden kulut, uusin ensin. */
export function ExpensesSection({
  propertyId,
  year,
  expenses,
  archived,
}: {
  propertyId: string;
  year: number;
  expenses: Expense[];
  archived: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState<Expense | 'new' | null>(null);
  const [removing, setRemoving] = useState<Expense | null>(null);
  const remove = useDeleteExpense(propertyId);
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<ExpenseCategory | null>(null);
  const toggle = (key: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Toistuvan kulun kuukaudet yhdeksi riviksi (avattavissa), muut kulut omina riveinään.
  type Group =
    | { kind: 'single'; key: string; expense: Expense }
    | { kind: 'recurring'; key: string; items: Expense[] };
  // Lajipainikkeet: kaikki tai yksi laji summineen.
  const sums = new Map<ExpenseCategory, number>();
  for (const x of expenses) sums.set(x.category, (sums.get(x.category) ?? 0) + x.amountCents);
  const visible = filter ? expenses.filter((x) => x.category === filter) : expenses;
  const groups: Group[] = [];
  const byTemplate = new Map<string, Expense[]>();
  for (const x of visible) {
    if (x.templateId && !x.edited) {
      const list = byTemplate.get(x.templateId);
      if (list) {
        list.push(x);
        continue;
      }
      const items = [x];
      byTemplate.set(x.templateId, items);
      groups.push({ kind: 'recurring', key: x.templateId, items });
    } else {
      groups.push({ kind: 'single', key: x.id, expense: x });
    }
  }

  const renderRow = (x: Expense) => (
    <li key={x.id} className={s.expenseRow}>
      <span className={s.expenseDate}>{formatDate(x.paidOn, i18n.language)}</span>
      <span className={s.main}>
        <span className={s.expenseTitle}>{x.description}</span>
        <span className={s.meta}>
          {t(`expenses.category.${x.category}`)}
          {x.depositCoveredCents > 0 &&
            ` · ${t('expenses.coveredMeta', { amount: formatCents(x.depositCoveredCents, i18n.language) })}`}
          {x.fileId && (
            <>
              {' · '}
              <a href={fileUrl(x.fileId)} target="_blank" rel="noreferrer">
                <Paperclip size={12} aria-hidden="true" /> {t('expenses.receipt')}
              </a>
            </>
          )}
          {x.maintenanceEntryId && (
            <>
              {' · '}
              <Link to={`/vuokranantaja/asunnot/${propertyId}/huolto`}>
                <Wrench size={12} aria-hidden="true" /> {t('expenses.inMaintenance')}
              </Link>
            </>
          )}
        </span>
      </span>
      <span className={s.amount}>{formatCents(x.amountCents, i18n.language)}</span>
      {!archived && (
        <span className={s.side}>
          <Button variant="ghost" icon aria-label={t('edit.edit')} onClick={() => setEditing(x)}>
            <Pencil size={15} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            icon
            aria-label={t('common.delete')}
            onClick={() => setRemoving(x)}
          >
            <Trash2 size={15} aria-hidden="true" />
          </Button>
        </span>
      )}
    </li>
  );

  return (
    <Section
      title={t('expenses.title')}
      action={
        archived ? undefined : (
          <Button variant="ghost" onClick={() => setEditing('new')}>
            <Plus size={15} aria-hidden="true" />
            {t('expenses.add')}
          </Button>
        )
      }
    >
      {expenses.length === 0 ? (
        <EmptyState title={t('expenses.emptyYear', { year })} text={t('expenses.emptyText')} />
      ) : (
        <>
          {sums.size > 1 && (
            <div className={s.chips} role="group" aria-label={t('expenses.categoryLabel')}>
              <button
                type="button"
                className={`${s.chip} ${filter === null ? s.chipActive : ''}`}
                aria-pressed={filter === null}
                onClick={() => setFilter(null)}
              >
                {t('expenses.all')}
              </button>
              {expenseCategories
                .filter((c) => sums.has(c))
                .map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`${s.chip} ${filter === c ? s.chipActive : ''}`}
                    aria-pressed={filter === c}
                    onClick={() => setFilter(filter === c ? null : c)}
                  >
                    {t(`expenses.category.${c}`)}
                  </button>
                ))}
            </div>
          )}
          <Panel>
            <ul className={s.list}>
              {groups.map((g) =>
                g.kind === 'single' ? (
                  renderRow(g.expense)
                ) : (
                  <li key={g.key} className={s.groupItem}>
                    <button
                      type="button"
                      className={s.compact}
                      aria-expanded={opened.has(g.key)}
                      onClick={() => toggle(g.key)}
                    >
                      <span className={s.expenseTitle}>
                        {g.items[0]!.description}
                        <Repeat
                          size={13}
                          aria-label={t('expenses.recurring')}
                          className={s.chevron}
                        />
                      </span>
                      <span className={s.meta}>
                        {t('expenses.groupMeta', {
                          count: g.items.length,
                          from: formatDate(g.items.at(-1)!.paidOn, i18n.language),
                          to: formatDate(g.items[0]!.paidOn, i18n.language),
                        })}
                      </span>
                      <span />
                      <span className={s.amount}>
                        {formatCents(
                          g.items.reduce((n, x) => n + x.amountCents, 0),
                          i18n.language,
                        )}
                      </span>
                      {opened.has(g.key) ? (
                        <ChevronUp size={16} aria-hidden="true" className={s.chevron} />
                      ) : (
                        <ChevronDown size={16} aria-hidden="true" className={s.chevron} />
                      )}
                    </button>
                    {opened.has(g.key) && (
                      <ul className={`${s.list} ${s.groupList}`}>{g.items.map(renderRow)}</ul>
                    )}
                  </li>
                ),
              )}
            </ul>
            <div className={s.listTotal}>
              <span>
                {filter
                  ? t('expenses.categoryTotal', { category: t(`expenses.category.${filter}`) })
                  : t('invoices.total')}
              </span>
              <span>
                {formatCents(
                  visible.reduce((n, x) => n + x.amountCents, 0),
                  i18n.language,
                )}
              </span>
            </div>
          </Panel>
        </>
      )}
      {editing && (
        <ExpenseDialog
          propertyId={propertyId}
          expense={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('expenses.removeTitle')}
      >
        <p>{removing?.templateId ? t('expenses.removeRecurringText') : t('expenses.removeText')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() => remove.mutate(removing!.id, { onSuccess: () => setRemoving(null) })}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
}

/** Esitäyttö huoltokirjan merkinnästä. */
export interface ExpensePrefill {
  paidOn: string;
  category: ExpenseCategory;
  description: string;
  amountCents: number | null;
  maintenanceEntryId: string;
  fileId: string | null;
  fileName?: string | null;
}

/**
 * Kulun kirjaus tai muokkaus. Uuden kulun voi merkitä toistuvaksi: silloin siitä tulee
 * pohja, josta kulu syntyy joka kuukausi samana päivänä.
 */
export function ExpenseDialog({
  propertyId,
  expense,
  prefill,
  onClose,
}: {
  propertyId: string;
  expense: Expense | null;
  prefill?: ExpensePrefill;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const save = useSaveExpense(propertyId);
  const createTemplate = useCreateExpenseTemplate(propertyId);
  const upload = useUploadFiles(propertyId);
  const base = expense ?? prefill;
  const [paidOn, setPaidOn] = useState(base?.paidOn ?? todayIso());
  const [category, setCategory] = useState<ExpenseCategory>(base?.category ?? 'other');
  const [description, setDescription] = useState(base?.description ?? '');
  const [amount, setAmount] = useState(
    base?.amountCents ? euros(base.amountCents, i18n.language) : '',
  );
  const [covered, setCovered] = useState(
    expense?.depositCoveredCents ? euros(expense.depositCoveredCents, i18n.language) : '',
  );
  const coveredCents = covered.trim() ? parseEuroToCents(covered) : 0;
  const [file, setFile] = useState<File | null>(null);
  const [keepFile, setKeepFile] = useState(Boolean(base?.fileId));
  const [recurring, setRecurring] = useState(false);
  // Huoltokirjaan: oletus kululajin mukaan, kunnes käyttäjä valitsee itse.
  const [toMaintenanceChoice, setToMaintenance] = useState<boolean | null>(null);
  const toMaintenance = toMaintenanceChoice ?? category in MAINTENANCE_KIND_FROM_EXPENSE;
  const [endMonth, setEndMonth] = useState('');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string>();
  const cents = parseEuroToCents(amount);
  const canRecur = !expense && !prefill;
  const startMonth = paidOn.slice(0, 7);
  const months = Array.from({ length: 36 }, (_, n) => addMonths(startMonth, n));
  const busy = save.isPending || createTemplate.isPending || upload.isPending;
  const existingName = expense?.fileName ?? prefill?.fileName;

  const errors = {
    paidOn: /^\d{4}-\d{2}-\d{2}$/.test(paidOn) ? undefined : t('errors.date'),
    amount: cents !== null && cents > 0 ? undefined : t('errors.amount'),
    covered:
      coveredCents === null || coveredCents < 0
        ? t('errors.amount')
        : cents !== null && coveredCents > cents
          ? t('errors.depositCovered')
          : undefined,
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    setError(undefined);
    if (Object.values(errors).some(Boolean)) return;
    try {
      if (recurring) {
        await createTemplate.mutateAsync({
          category,
          description: description.trim() || t(`expenses.category.${category}`),
          amountCents: cents!,
          day: Number(paidOn.slice(8, 10)),
          startMonth,
          endMonth: endMonth || null,
        });
      } else {
        let fileId = keepFile ? (base?.fileId ?? null) : null;
        if (file) {
          const [stored] = await upload.mutateAsync({
            files: [file],
            folder: 'receipt',
            shared: false,
          });
          fileId = stored?.id ?? null;
        }
        await save.mutateAsync({
          id: expense?.id,
          body: {
            paidOn,
            category,
            description: description.trim() || t(`expenses.category.${category}`),
            amountCents: cents!,
            fileId,
            maintenanceEntryId: expense?.maintenanceEntryId ?? prefill?.maintenanceEntryId ?? null,
            depositCoveredCents: coveredCents ?? 0,
            addToMaintenance: canRecur && toMaintenance,
          },
        });
      }
      onClose();
    } catch (e) {
      setError(errorKey(e));
    }
  }

  return (
    <Dialog open onClose={onClose} title={expense ? t('expenses.editTitle') : t('expenses.add')}>
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <div className={ps.formRow}>
          <TextField
            label={recurring ? t('expenses.firstPaidOn') : t('expenses.paidOn')}
            type="date"
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
            error={tried ? errors.paidOn : undefined}
          />
          <TextField
            label={t('expenses.amount')}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={tried ? errors.amount : undefined}
          />
        </div>
        <SelectField
          label={t('expenses.categoryLabel')}
          hint={t(`expenses.hint.${category}`)}
          value={category}
          onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
        >
          {expenseCategories.map((c) => (
            <option key={c} value={c}>
              {t(`expenses.category.${c}`)}
            </option>
          ))}
        </SelectField>
        <TaxTreatment
          propertyId={propertyId}
          category={category}
          amountCents={cents}
          paidOn={paidOn}
        />
        <TextField
          label={t('expenses.description')}
          hint={t('expenses.descriptionHint')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {!recurring && (
          <TextField
            label={t('expenses.depositCovered')}
            hint={t('expenses.depositCoveredHint')}
            inputMode="decimal"
            value={covered}
            onChange={(e) => setCovered(e.target.value)}
            error={tried ? errors.covered : undefined}
          />
        )}
        {canRecur && !recurring && (
          <label className={s.check}>
            <input
              type="checkbox"
              checked={toMaintenance}
              onChange={(e) => setToMaintenance(e.target.checked)}
            />
            <span>{t('expenses.toMaintenance')}</span>
          </label>
        )}
        {canRecur && (
          <label className={s.check}>
            <input
              type="checkbox"
              checked={recurring}
              onChange={(e) => setRecurring(e.target.checked)}
            />
            <span>{t('expenses.recurringToggle')}</span>
          </label>
        )}
        {recurring ? (
          <>
            <SelectField
              label={t('invoices.to')}
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
            >
              <option value="">{t('invoices.untilFurther')}</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m, i18n.language)}
                </option>
              ))}
            </SelectField>
            <Notice>
              {t('expenses.recurringHint', {
                day: Number(paidOn.slice(8, 10)) || 1,
                month: monthLabel(startMonth, i18n.language),
              })}
            </Notice>
          </>
        ) : (
          <div style={{ display: 'grid', gap: '0.4rem' }}>
            {keepFile && base?.fileId && !file ? (
              <p className={s.meta}>
                <Paperclip size={13} aria-hidden="true" /> {existingName ?? t('expenses.receipt')}{' '}
                <button
                  type="button"
                  className={`${s.linkButton} ${s.linkDanger}`}
                  onClick={() => setKeepFile(false)}
                >
                  {t('expenses.changeReceipt')}
                </button>
              </p>
            ) : (
              <TextField
                label={t('expenses.receiptLabel')}
                hint={t('expenses.receiptHint')}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            )}
          </div>
        )}
        {error && <Alert>{t(error)}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Miten kulu käsitellään verotuksessa: kerralla, poistoina tai eri kohdassa. Remontista
 * varoitetaan, jos se on maksettu ennen vuokrausta tai vuokrauksen päätyttyä.
 */
function TaxTreatment({
  propertyId,
  category,
  amountCents,
  paidOn,
}: {
  propertyId: string;
  category: ExpenseCategory;
  amountCents: number | null;
  paidOn: string;
}) {
  const { t, i18n } = useTranslation();
  const { data: leases } = useLeases(propertyId);
  const treatment = expenseTaxTreatment(category, amountCents ?? 0);
  const money = (c: number) => formatCents(c, i18n.language);
  const hasAmount = amountCents !== null && amountCents > 0;

  let text: string;
  switch (treatment.kind) {
    case 'improvement':
      text = hasAmount
        ? t('expenses.tax.improvement', { amount: money(treatment.annualCents) })
        : t('expenses.tax.improvementNoAmount');
      break;
    case 'furniturePool':
      text = t('expenses.tax.furniturePool', { amount: money(treatment.firstYearCents) });
      break;
    case 'capitalCharge':
      text = t('expenses.tax.capitalCharge');
      break;
    case 'interest':
      text = t('expenses.tax.interest');
      break;
    default:
      text =
        category === 'furniture'
          ? t('expenses.tax.furnitureImmediate')
          : t('expenses.tax.immediate');
  }

  const timing =
    (category === 'repair' || category === 'improvement') &&
    leases &&
    /^\d{4}-\d{2}-\d{2}$/.test(paidOn)
      ? rentalTiming(
          paidOn,
          leases
            .filter((l) => l.status !== 'draft')
            .map((l) => ({
              start: l.startDate,
              end: l.endDate ?? l.endedAt?.slice(0, 10) ?? null,
            })),
        )
      : null;

  return (
    <div className={s.taxTreatment}>
      <p>
        <strong>{t('expenses.tax.label')}</strong> {text}{' '}
        <a href={CATEGORY_LINK[category]} target="_blank" rel="noreferrer">
          {t('tax.readMore')}
          <ExternalLink size={12} aria-hidden="true" />
        </a>
      </p>
      {timing && <p className={s.taxWarning}>{t(`expenses.tax.timing.${timing}`)}</p>}
    </div>
  );
}

/** Toistuvat kulut: summan muutos uudella pohjalla, päättyminen ja poisto. */
export function RecurringExpenses({
  propertyId,
  archived,
}: {
  propertyId: string;
  archived: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { data: templates } = useExpenseTemplates(propertyId);
  const [changing, setChanging] = useState<ExpenseTemplate | null>(null);
  const [ending, setEnding] = useState<ExpenseTemplate | null>(null);
  const [removing, setRemoving] = useState<ExpenseTemplate | null>(null);
  const remove = useDeleteExpenseTemplate(propertyId);
  if (!templates || templates.length === 0) return null;
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return (
    <Section title={t('expenses.recurringTitle')} note={t('expenses.recurringNote')}>
      <Panel>
        {templates.map((tpl) => {
          const ended = tpl.endMonth !== null && tpl.endMonth < current;
          return (
            <div key={tpl.id} className={s.templateRow}>
              <div className={s.main}>
                <span className={s.month} style={ended ? { color: 'var(--muted)' } : undefined}>
                  {tpl.description} ·{' '}
                  {t('lease.perMonth', { amount: formatCents(tpl.amountCents, i18n.language) })}
                </span>
                <span className={s.meta}>
                  {t(`expenses.category.${tpl.category}`)} ·{' '}
                  {t('lease.dueDayValue', { day: tpl.day })} ·{' '}
                  {monthLabel(tpl.startMonth, i18n.language)} –{' '}
                  {tpl.endMonth
                    ? monthLabel(tpl.endMonth, i18n.language)
                    : t('invoices.untilFurther')}
                </span>
              </div>
              {!archived && (
                <div
                  className={s.actions}
                  style={{ gridColumn: 'auto', justifyContent: 'flex-end' }}
                >
                  {!ended && (
                    <>
                      <button
                        type="button"
                        className={s.linkButton}
                        onClick={() => setChanging(tpl)}
                      >
                        {t('expenses.changeAmount')}
                      </button>
                      <button type="button" className={s.linkButton} onClick={() => setEnding(tpl)}>
                        {t('expenses.end')}
                      </button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    icon
                    aria-label={t('common.delete')}
                    onClick={() => setRemoving(tpl)}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </Panel>
      {changing && (
        <ChangeAmountDialog
          propertyId={propertyId}
          template={changing}
          onClose={() => setChanging(null)}
        />
      )}
      {ending && (
        <EndDialog propertyId={propertyId} template={ending} onClose={() => setEnding(null)} />
      )}
      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('expenses.removeRecurringTitle')}
      >
        <p>{t('expenses.removeTemplateText')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() => remove.mutate(removing!.id, { onSuccess: () => setRemoving(null) })}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
}

function ChangeAmountDialog({
  propertyId,
  template,
  onClose,
}: {
  propertyId: string;
  template: ExpenseTemplate;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const create = useCreateExpenseTemplate(propertyId);
  const first = addMonths(template.startMonth, 1);
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const months = Array.from({ length: 30 }, (_, n) => addMonths(first, n)).filter(
    (m) => !template.endMonth || m <= addMonths(template.endMonth, 1),
  );
  const [from, setFrom] = useState(months.includes(current) ? current : months[0]!);
  const [amount, setAmount] = useState(euros(template.amountCents, i18n.language));
  const cents = parseEuroToCents(amount);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (cents === null || cents < 1) return;
    create.mutate(
      {
        category: template.category,
        description: template.description,
        amountCents: cents,
        day: template.day,
        startMonth: from,
        endMonth: template.endMonth && template.endMonth >= from ? template.endMonth : null,
        replacesTemplateId: template.id,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('expenses.changeAmountTitle', { name: template.description })}
    >
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <p className={s.meta}>{t('expenses.changeAmountHint')}</p>
        <div className={ps.formRow}>
          <SelectField
            label={t('invoices.from')}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m, i18n.language)}
              </option>
            ))}
          </SelectField>
          <TextField
            label={t('expenses.newAmount')}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={cents === null || cents < 1 ? t('errors.amount') : undefined}
          />
        </div>
        {create.isError && <Alert>{t(errorKey(create.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function EndDialog({
  propertyId,
  template,
  onClose,
}: {
  propertyId: string;
  template: ExpenseTemplate;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const update = useUpdateExpenseTemplate(propertyId);
  const months = Array.from({ length: 36 }, (_, n) => addMonths(template.startMonth, n));
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [end, setEnd] = useState(
    template.endMonth ?? (months.includes(current) ? current : months[0]!),
  );

  return (
    <Dialog open onClose={onClose} title={t('expenses.endTitle', { name: template.description })}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(
            { id: template.id, body: { endMonth: end || null } },
            { onSuccess: onClose },
          );
        }}
        noValidate
        style={{ display: 'grid', gap: '1.1rem' }}
      >
        <p className={s.meta}>{t('expenses.endHint')}</p>
        <SelectField
          label={t('expenses.lastMonth')}
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        >
          <option value="">{t('invoices.untilFurther')}</option>
          {months.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m, i18n.language)}
            </option>
          ))}
        </SelectField>
        {update.isError && <Alert>{t(errorKey(update.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
